import React from "react";

/* ============================================================
   The Debt Guy — "Maca v2" shared design kit.
   Bold Archivo Black, electric-cobalt accent, hand-drawn lime
   highlights, pill CTAs with hard shadows, glassy 3D art.
   ============================================================ */

export const INK = "#141414";
export const CREAM = "#FAF7EF";
export const LIME = "#D9F154";
export const COBALT = "#2F2BEB";
export const PURPLE = "#3B1E70";
export const PURPLE_ACCENT = "#7C5CE0";

/* Inject shared CSS + fonts once. Idempotent-ish (harmless if repeated). */
export const MacaKit = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Caveat:wght@500;600;700&display=swap');
    .mk-pill {
      display: inline-flex; align-items: center; gap: 12px;
      font-family: 'Archivo', sans-serif; font-weight: 800;
      letter-spacing: 0.05em; text-transform: uppercase; text-decoration: none;
      border: 2.5px solid ${INK}; border-radius: 999px; cursor: pointer;
      padding: 16px 28px; font-size: 13px; line-height: 1; white-space: nowrap;
      transition: transform 0.12s ease, box-shadow 0.12s ease;
    }
    .mk-pill:hover { transform: translate(-2px,-2px); }
    .mk-pill:active { transform: translate(2px,2px); }
    .mk-caveat { font-family: 'Caveat', cursive; }
    @keyframes mk-livedot { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.45;transform:scale(.82)} }
    .mk-livedot { animation: mk-livedot 1.4s ease-in-out infinite; }
    @keyframes mk-fadeup { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
    .mk-fadeup { animation: mk-fadeup .8s cubic-bezier(.16,1,.3,1) both; }
  `}</style>
);

const ArrowR = ({ c = "currentColor", s = 20 }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 12h15M13 6l6 6-6 6" />
  </svg>
);
const Bolt = ({ c = "currentColor", s = 16 }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" fill={c}><path d="M13 2 4 14h6l-1 8 9-12h-6z" /></svg>
);

const PILL = {
  lime:  { background: LIME,  color: INK,       shadow: `5px 5px 0 ${INK}` },
  ink:   { background: INK,   color: "#FFFFFF", shadow: `5px 5px 0 ${COBALT}` },
  cobalt:{ background: COBALT,color: "#FFFFFF", shadow: `5px 5px 0 ${INK}` },
  ghost: { background: "#FFFFFF", color: INK,   shadow: `5px 5px 0 ${INK}` },
};

/** Pill CTA — link or button, hard shadow, optional leading bolt / trailing arrow. */
export const PillCTA = ({
  children, href, onClick, variant = "lime", bolt = false, arrow = true,
  leadingArrow = false, testid, big = false, style = {},
}) => {
  const v = PILL[variant] || PILL.lime;
  const Comp = href ? "a" : "button";
  const props = href ? { href } : { type: "button", onClick };
  return (
    <Comp
      {...props}
      data-testid={testid}
      className="mk-pill"
      style={{
        background: v.background, color: v.color, boxShadow: v.shadow,
        padding: big ? "19px 34px" : "16px 26px", fontSize: big ? 14.5 : 13, ...style,
      }}
    >
      {bolt && (
        <span style={{ display: "inline-flex", width: 26, height: 26, borderRadius: "50%", background: variant === "lime" ? INK : LIME, color: variant === "lime" ? LIME : INK, alignItems: "center", justifyContent: "center", marginRight: 2 }}>
          <Bolt s={14} c={variant === "lime" ? LIME : INK} />
        </span>
      )}
      {leadingArrow && <ArrowR c={v.color} s={big ? 22 : 20} />}
      {leadingArrow && <span style={{ width: 1.5, height: 20, background: v.color, opacity: 0.4 }} />}
      <span>{children}</span>
      {arrow && !leadingArrow && <ArrowR c={v.color} s={big ? 22 : 20} />}
    </Comp>
  );
};

/** Hand-drawn lime highlight behind/under a word. */
export const Highlight = ({ children, color = LIME, thickness = 14 }) => (
  <span style={{ position: "relative", display: "inline-block", whiteSpace: "nowrap" }}>
    <svg viewBox="0 0 300 40" preserveAspectRatio="none" aria-hidden
      style={{ position: "absolute", left: "-2%", bottom: "-0.06em", width: "104%", height: "0.42em", zIndex: 0, pointerEvents: "none" }}>
      <path d="M8 27 C 70 14, 150 33, 214 21 S 288 15, 294 24" fill="none" stroke={color} strokeWidth={thickness} strokeLinecap="round" opacity="0.95" />
    </svg>
    <span style={{ position: "relative", zIndex: 1 }}>{children}</span>
  </span>
);

/** Small comic "spark" strokes. */
export const SparkLines = ({ color = COBALT, size = 44, style = {} }) => (
  <svg width={size} height={size} viewBox="0 0 44 44" fill="none" stroke={color} strokeWidth="3.4" strokeLinecap="round" style={style} aria-hidden>
    <path d="M30 6 L24 16" /><path d="M40 12 L31 19" /><path d="M42 24 L32 26" />
  </svg>
);

/** Handwritten cobalt annotation with optional curved arrow. */
export const Scribble = ({ children, arrow = "none", size = 22, color = COBALT, style = {} }) => {
  const arrows = {
    "down-left":  "M40 6 C 20 18, 10 34, 6 52 M6 52 L14 44 M6 52 L2 42",
    "down-right": "M6 6 C 26 18, 36 34, 40 52 M40 52 L32 44 M40 52 L44 42",
    down:         "M22 4 C 16 20, 26 36, 22 52 M22 52 L15 44 M22 52 L29 44",
  };
  return (
    <div style={{ display: "inline-block", ...style }} aria-hidden>
      <div className="mk-caveat" style={{ color, fontSize: size, fontWeight: 600, lineHeight: 1.05, transform: "rotate(-4deg)" }}>
        {children}
      </div>
      {arrow !== "none" && (
        <svg width="46" height="58" viewBox="0 0 46 58" fill="none" stroke={color} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: 2 }}>
          <path d={arrows[arrow]} />
        </svg>
      )}
    </div>
  );
};

/* ---------- Glass 3D art (stacked cards + icon/label) ---------- */
export const Glyph = {
  chart: (c = COBALT) => (
    <svg width="72" height="72" viewBox="0 0 48 48" fill="none">
      <path d="M8 40 L40 40" stroke={c} strokeWidth="3" strokeLinecap="round" />
      <rect x="12" y="26" width="6" height="12" rx="2" fill={c} />
      <rect x="21" y="20" width="6" height="18" rx="2" fill={c} />
      <rect x="30" y="12" width="6" height="26" rx="2" fill={c} />
      <path d="M10 24 L20 16 L28 21 L40 8" stroke={INK} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <path d="M33 8 L40 8 L40 15" stroke={INK} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  ),
  pie: (c = COBALT) => (
    <svg width="72" height="72" viewBox="0 0 48 48" fill="none">
      <circle cx="24" cy="24" r="16" fill={c} opacity="0.25" />
      <path d="M24 24 L24 8 A16 16 0 0 1 40 24 Z" fill={c} />
      <circle cx="24" cy="24" r="16" stroke={INK} strokeWidth="2" fill="none" />
    </svg>
  ),
  lock: (c = COBALT) => (
    <svg width="72" height="72" viewBox="0 0 48 48" fill="none">
      <rect x="12" y="22" width="24" height="18" rx="4" fill={c} />
      <path d="M17 22 V16 a7 7 0 0 1 14 0" stroke={INK} strokeWidth="3" fill="none" strokeLinecap="round" />
      <circle cx="24" cy="30" r="2.6" fill="#FFFFFF" />
      <rect x="23" y="31" width="2" height="5" rx="1" fill="#FFFFFF" />
    </svg>
  ),
  users: (c = COBALT) => (
    <svg width="76" height="76" viewBox="0 0 48 48" fill="none">
      <circle cx="19" cy="17" r="6" fill={c} />
      <path d="M8 38 a11 11 0 0 1 22 0 Z" fill={c} />
      <circle cx="33" cy="19" r="5" fill={c} opacity="0.8" />
      <path d="M28 38 a9 9 0 0 1 16 -3 L44 38 Z" fill={c} opacity="0.8" />
    </svg>
  ),
  file: (c = COBALT) => (
    <svg width="72" height="72" viewBox="0 0 48 48" fill="none">
      <rect x="12" y="8" width="24" height="32" rx="3" fill="#FFFFFF" stroke={INK} strokeWidth="2" />
      <path d="M18 30 L26 22 L31 26 L38 18" stroke={c} strokeWidth="2.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
};

/**
 * Glassy stacked-card artwork with an accent backdrop, spark lines and a
 * centered glyph/label. Used across hero, problem cards, scarcity, CTA.
 */
export const GlassArt = ({
  accent = COBALT, glyph = "chart", glyphColor, label, sublabel, width = 300, height = 280,
  spark = true, sparkColor = COBALT,
}) => {
  const u = width / 300;
  const card = (o) => ({
    position: "absolute", borderRadius: 22 * u, ...o,
  });
  const glass = {
    background: "rgba(255,255,255,0.62)", border: "1.5px solid rgba(255,255,255,0.85)",
    backdropFilter: "blur(9px)", WebkitBackdropFilter: "blur(9px)",
    boxShadow: `0 ${22 * u}px ${44 * u}px rgba(20,20,20,0.20)`,
  };
  const glyphNode = (Glyph[glyph] || Glyph.chart)(glyphColor || accent);
  return (
    <div style={{ position: "relative", width, height, flexShrink: 0 }} aria-hidden>
      {/* accent angular backdrop */}
      <div style={card({ top: 24 * u, right: -14 * u, width: 200 * u, height: 220 * u, background: accent, transform: "rotate(9deg)", borderRadius: 30 * u, opacity: 0.92 })} />
      {/* ink offset card */}
      <div style={card({ top: 54 * u, right: 22 * u, width: 150 * u, height: 168 * u, background: INK, opacity: 0.9 })} />
      {/* back glass */}
      <div style={card({ top: 34 * u, right: 34 * u, width: 150 * u, height: 168 * u, ...glass })} />
      {/* front glass */}
      <div style={card({
        top: 16 * u, right: 58 * u, width: 158 * u, height: 176 * u, ...glass,
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8 * u, padding: 14 * u, textAlign: "center",
      })}>
        {glyphNode}
        {label && <div style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 900, fontSize: 22 * u, letterSpacing: "-0.02em", color: INK, lineHeight: 1 }}>{label}</div>}
        {sublabel && <div style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 700, fontSize: 9 * u, letterSpacing: "0.14em", textTransform: "uppercase", color: INK, opacity: 0.6 }}>{sublabel}</div>}
      </div>
      {spark && <SparkLines color={sparkColor} size={46 * u} style={{ position: "absolute", top: 4 * u, left: 6 * u }} />}
    </div>
  );
};
